import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Subscription tier — mirrors `SubscriptionTierCode` from `@bravemove/types/subscription`
 * (`'free' | 'starter' | 'pro' | 'premium'`). Kept local — `@bravemove/types` is NOT a
 * dep of `@bravemove/ui-web`. Spec source:
 * [`docs/design/screens/revenue-lib.jsx`](../../../../../docs/design/screens/revenue-lib.jsx)
 * §3 `PlanTierBadge` (line 437). Renders the tier LABEL only — never the price.
 */
export type PlanTier = 'free' | 'starter' | 'pro' | 'premium';
export type PlanTierBadgeSize = 'sm' | 'md';
export type PlanTierCadence = 'monthly' | 'annual';

export interface PlanTierBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly tier: PlanTier;
  readonly size?: PlanTierBadgeSize;
  /** Optional cadence suffix — renders `· MO` / `· YR` at reduced opacity. */
  readonly cadence?: PlanTierCadence;
  /** Override the default tier label. */
  readonly label?: string;
}

/**
 * Tier→tone map. Tailwind can't see dynamically-built class names through its
 * purge, so every tone class is a static literal here. Tones:
 *  - `free`    → ink-3 (neutral)
 *  - `starter` → info
 *  - `pro`     → accent
 *  - `premium` → tertiary
 */
const TIER_STYLE: Record<PlanTier, { box: string; defaultLabel: string }> = {
  free: {
    box: 'bg-ink-3/10 text-ink border border-ink-3/40',
    defaultLabel: 'Free',
  },
  starter: {
    box: 'bg-info/[0.09] text-ink border border-info/40',
    defaultLabel: 'Starter',
  },
  pro: {
    box: 'bg-accent/[0.09] text-ink border border-accent/40',
    defaultLabel: 'Pro',
  },
  premium: {
    box: 'bg-tertiary/[0.09] text-ink border border-tertiary/40',
    defaultLabel: 'Premium',
  },
};

const SIZE: Record<PlanTierBadgeSize, string> = {
  sm: 'px-[7px] py-[2px] gap-1 text-[9px]',
  md: 'px-[9px] py-[3px] gap-1.5 text-[10px]',
};

/**
 * F38 — `PlanTierBadge` (shared primitive). Subscription tier label pill for the
 * portal billing surfaces + admin venue-subscription list. Pairs with
 * `SubStateBadge` (lifecycle) in the revenue-portal top bar.
 *
 * @example
 *   <PlanTierBadge tier="pro" />
 *   <PlanTierBadge tier="premium" cadence="annual" />
 *   <PlanTierBadge tier="starter" size="sm" />
 */
export function PlanTierBadge({
  tier,
  size = 'md',
  cadence,
  label,
  className,
  ...rest
}: PlanTierBadgeProps) {
  const style = TIER_STYLE[tier];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={`Plan tier: ${text}`}
      data-testid={`plan-tier-badge-${tier}`}
      className={cn(
        'inline-flex items-center rounded-[5px] uppercase whitespace-nowrap align-middle font-mono font-extrabold tracking-[0.12em]',
        style.box,
        SIZE[size],
        className,
      )}
      {...rest}
    >
      <span>{text}</span>
      {cadence ? <span className="opacity-70">· {cadence === 'annual' ? 'YR' : 'MO'}</span> : null}
    </span>
  );
}
